<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="page">
	@switch (_pageState()) {
		@case (_PageStates.UPLOAD) {
			<div class="upload">
				<div class="title">
					<span class="header">Device detail report</span>
					<span class="sub-header">
						Upload your device list in excel format. Use the template as a reference to
						correctly visualize the data.
					</span>
				</div>
				<globalictcc-button
					[textOnly]="true"
					label="Download template"
					iconName="download"
					(handleClick)="onDownloadTemplate()"
				/>

				@if (authorizationService.hasPermission(permissions.AI_UPLOAD)) {
					<globalictcc-upload-file
						[errorMessage]="sameNameErrorMessage()"
						[acceptedFilesExtensions]="['.xlsx']"
						(handleUpload)="onFileUpload($event)"
						(handleRemove)="onRemoveFile()"
					/>

					<globalictcc-button
						label="Confirm"
						[disabled]="!uploadedFile() || isUploadDisabled()"
						(handleClick)="onConfirm()"
					/>
				} @else {
					<span class="sub-header">No AI reports are available.</span>
				}
			</div>
		}
		@case (_PageStates.LOADING) {
			<div class="loading">
				<globalictcc-spinner />

				<span class="message">Loading...</span>
			</div>
		}
		@case (_PageStates.ERROR) {
			<div class="error">
				<span class="message"
					>There was an error uploading document. Please try again.</span
				>
				@if (authorizationService.hasPermission(permissions.AI_UPLOAD)) {
					<globalictcc-button
						label="Retry"
						(handleClick)="onUploadDeviceList()"
					/>
				}
			</div>
		}
		@case (_PageStates.MAIN) {
			<div class="main">
				@if (
					authorizationService.canSee(grants.emissionsByApplication) ||
					authorizationService.canSee(grants.waterByApplication)
				) {
					<div class="period">
						<globalictcc-dropdown
							label="Reporting period"
							[formControl]="periodControl"
							[options]="periodOptions"
						/>
						<globalictcc-button
							label="Apply"
							(handleClick)="onApply()"
							[disabled]="!periodControl.value"
						/>
					</div>
				}
				<div class="top-actions">
					<div class="uploaded-file-info">
						<globalictcc-dropdown
							[isHuge]="true"
							[options]="filesOptions"
							[formControl]="fileControl"
							(handleSelectionChange)="onFileChange($event)"
						/>

						@if (
							authorizationService.hasPermission(permissions.AI_RENAME) ||
							authorizationService.hasPermission(permissions.AI_DELETE)
						) {
							<div class="actions">
								@if (authorizationService.hasPermission(permissions.AI_RENAME)) {
									<globalictcc-button
										label="Rename"
										iconName="edit"
										[textOnly]="true"
										(handleClick)="onRename()"
									/>
								}
								@if (authorizationService.hasPermission(permissions.AI_DELETE)) {
									<globalictcc-button
										label="Delete"
										iconName="delete"
										[textOnly]="true"
										(handleClick)="onDelete()"
									/>
								}
							</div>
						}
					</div>

					@if (authorizationService.hasPermission(permissions.AI_UPLOAD)) {
						<globalictcc-button
							label="Upload file"
							(handleClick)="onUploadDeviceList()"
						/>
					}
				</div>

				@if (
					authorizationService.canSee(grants.co2Kpi) ||
					authorizationService.canSee(grants.waterUsageKpi) ||
					authorizationService.canSee(grants.totalAppsKpi)
				) {
					<div class="kpis-section">
						@if (authorizationService.canSee(grants.co2Kpi)) {
							@let generalInformation = generalInformation$ | async;
							<div class="total-co2">
								<globalictcc-kpi
									label="Total CO2e Emissions"
									[value]="generalInformation?.data?.totalCo2Emission?.value ?? 0"
									[unit]="generalInformation?.data?.totalCo2Emission?.unit ?? ''"
								/>
							</div>
						}
						@if (authorizationService.canSee(grants.waterUsageKpi)) {
							@let generalInformation = generalInformation$ | async;
							<div class="total-water-usage">
								<globalictcc-kpi
									label="Total Water Usage"
									[value]="generalInformation?.data?.totalWaterUsage?.value ?? 0"
									[unit]="generalInformation?.data?.totalWaterUsage?.unit ?? ''"
								/>
							</div>
						}
						@if (authorizationService.canSee(grants.totalAppsKpi)) {
							@let generalInformation = generalInformation$ | async;
							<div class="count-applications">
								<globalictcc-kpi
									label="Total Applications"
									[value]="
										generalInformation?.data?.countApplications?.value ?? 0
									"
									[unit]="generalInformation?.data?.countApplications?.unit ?? ''"
								/>
							</div>
						}
					</div>
				}

				@if (
					authorizationService.canSee(grants.emissionsByApplication) ||
					authorizationService.canSee(grants.waterByApplication)
				) {
					<section class="apps-performance">
						@if (authorizationService.canSee(grants.emissionsByApplication)) {
							@let emissionsByApplication = appsPerformance$ | async;
							<div class="chart-block">
								<div class="section-title">
									<globalictcc-button
										label="Export chart"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												appsPerformanceChartRef.instance,
												'ai_apps_emissions_over_time'
											)
										"
									/>
								</div>

								<div class="chart-wrapper">
									<globalictcc-chart
										#appsPerformanceChartRef
										chartType="line"
										[data]="emissionsByApplication?.data?.chart"
										[isLoading]="emissionsByApplication?.isLoading ?? true"
										[hasError]="
											emissionsByApplication?.hasError ||
											emissionsByApplication?.data?.hasError
										"
										[isLegendVisible]="true"
										[colors]="appsPerformanceChartColors"
										[configOverrides]="appsPerformanceChartOverrides"
										height="400px"
									/>
								</div>
							</div>
						}
						@if (authorizationService.canSee(grants.waterByApplication)) {
							@let waterByApplication = appsWaterConsumption$ | async;
							<div class="chart-block">
								<div class="section-title">
									<globalictcc-button
										label="Export chart"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												appsWaterConsumptionChartRef.instance,
												'ai_apps_water_consumption_over_time'
											)
										"
									/>
								</div>

								<div class="chart-wrapper">
									<globalictcc-chart
										#appsWaterConsumptionChartRef
										chartType="line"
										[data]="waterByApplication?.data?.chart"
										[isLoading]="waterByApplication?.isLoading ?? true"
										[hasError]="
											waterByApplication?.hasError ||
											waterByApplication?.data?.hasError
										"
										[isLegendVisible]="true"
										[colors]="appsWaterConsumptionChartColors"
										[configOverrides]="appsWaterConsumptionChartOverrides"
										height="400px"
									/>
								</div>
							</div>
						}
					</section>
				}

				@if (authorizationService.canSee(grants.modelImpacts)) {
					@let modelEmissions = modelEmissions$ | async;
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								Model Environmental Impacts
								<!-- TODO: add tooltip with explanation of the chart -->
								<!-- <span class="icon-warning"></span> -->
							</span>

							<globalictcc-pagination
								[currentPage]="modelEmissionsCurrentPage"
								[totalItems]="modelEmissionsTotalElements"
								[pageSize]="modelEmissionsPageSize"
								[totalPages]="modelEmissionsTotalPages"
								[pageItems]="modelEmissionsPageItems"
								(handlePreviousPage)="onModelEmissionsPreviousPage()"
								(handleNextPage)="onModelEmissionsNextPage()"
							/>
						</div>

						<div class="table-chart single">
							<div class="table">
								<globalictcc-table
									[gridOptions]="gridOptions"
									[hasError]="!!modelEmissions?.hasError"
								/>
							</div>
						</div>
					</section>
				}

				@if (authorizationService.canSee(grants.emissionsByCompany)) {
					@let emissionsByCompany = environmentalImpactByCompany$ | async;
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								Emissions by company <span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										totalImpactChart.instance,
										'total_environmental_impact_by_company'
									)
								"
							/>
						</div>

						<div class="filter">
							<globalictcc-dropdown
								label="Company"
								[options]="companyOptions"
								[multi]="true"
								[formControl]="companyControl"
							/>

							<globalictcc-button
								label="Apply"
								(handleClick)="onApplyTotalImpactFilter()"
							/>
						</div>

						<div class="table-chart">
							<div class="table">
								<globalictcc-table
									[gridOptions]="totalImpactGridOptions"
									[hasError]="
										!!emissionsByCompany?.hasError || totalImpactTableHasError
									"
									(handleTableRefresh)="onTotalImpactTableRefresh()"
								/>
							</div>

							<globalictcc-chart
								#totalImpactChart
								chartType="pie"
								height="471px"
								labelPosition="outside"
								[isTitleVisible]="false"
								[data]="totalImpactPieChart?.chart"
								[hasError]="
									emissionsByCompany?.hasError || totalImpactPieChart?.hasError
								"
								[isLoading]="emissionsByCompany?.isLoading ?? true"
								[colors]="totalImpactPieChartColors"
								[configOverrides]="totalImpactPieChartOverrides"
								(handleChartRefresh)="onTotalImpactChartRefresh()"
							/>
						</div>
					</section>
				}
				@if (authorizationService.canSee(grants.emissionsByModel)) {
					@let emissionsByModel = environmentalImpactByModel$ | async;
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								Emissions by model <span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										byModelChart.instance,
										'total_environmental_impact_by_model'
									)
								"
							/>
						</div>

						<div class="filter">
							<globalictcc-dropdown
								label="Model"
								[options]="modelOptions"
								[multi]="true"
								[formControl]="modelControl"
							/>

							<globalictcc-button
								label="Apply"
								(handleClick)="onApplyByModelFilter()"
							/>
						</div>

						<div class="table-chart">
							<div class="table">
								<globalictcc-table
									[gridOptions]="byModelGridOptions"
									[hasError]="
										!!emissionsByModel?.hasError || byModelTableHasError
									"
									(handleTableRefresh)="onByModelTableRefresh()"
								/>
							</div>

							<globalictcc-chart
								#byModelChart
								chartType="pie"
								height="471px"
								labelPosition="outside"
								[isTitleVisible]="false"
								[data]="byModelPieChart?.chart"
								[hasError]="emissionsByModel?.hasError || byModelPieChart?.hasError"
								[isLoading]="emissionsByModel?.isLoading ?? true"
								[colors]="byModelPieChartColors"
								[configOverrides]="byModelPieChartOverrides"
								(handleChartRefresh)="onByModelChartRefresh()"
							/>
						</div>
					</section>
				}
				@if (authorizationService.canSee(grants.waterUsageByModel)) {
					@let waterUsageByModel = waterUsageByModel$ | async;
					<section class="chart">
						<div class="section-title">
							<span class="chart-title">
								Water usage by model <span class="icon-warning"></span>
							</span>

							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										waterUsageByModelChart.instance,
										'total_water_usage_by_model'
									)
								"
							/>
						</div>

						<div class="filter">
							<globalictcc-dropdown
								label="Model"
								[options]="waterUsageModelOptions"
								[multi]="true"
								[formControl]="waterUsageModelControl"
							/>

							<globalictcc-button
								label="Apply"
								(handleClick)="onApplyWaterUsageByModelFilter()"
							/>
						</div>

						<div class="table-chart">
							<div class="table">
								<globalictcc-table
									[gridOptions]="waterUsageByModelGridOptions"
									[hasError]="
										!!waterUsageByModel?.hasError ||
										waterUsageByModelTableHasError
									"
									(handleTableRefresh)="onWaterUsageByModelTableRefresh()"
								/>
							</div>

							<globalictcc-chart
								#waterUsageByModelChart
								chartType="pie"
								height="471px"
								labelPosition="outside"
								[isTitleVisible]="false"
								[data]="waterUsageByModelPieChart?.chart"
								[hasError]="
									waterUsageByModel?.hasError ||
									waterUsageByModelPieChart?.hasError
								"
								[isLoading]="waterUsageByModel?.isLoading ?? true"
								[colors]="waterUsageByModelPieChartColors"
								[configOverrides]="waterUsageByModelPieChartOverrides"
								(handleChartRefresh)="onWaterUsageByModelChartRefresh()"
							/>
						</div>
					</section>
				}

				@if (authorizationService.canSee(grants.mostEfficientModelCo2)) {
					@let best10Emissions = best10Emissions$ | async;
					<section class="chart">
						<div class="section-title">
							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										best10EmissionsChart.instance,
										'best_10_models_emissions_per_token'
									)
								"
							/>
						</div>

						<div class="chart-wrapper">
							<globalictcc-chart
								#best10EmissionsChart
								chartType="barY"
								height="400px"
								[data]="best10Emissions?.data?.chart"
								[isLoading]="best10Emissions?.isLoading ?? true"
								[hasError]="
									best10Emissions?.hasError || best10Emissions?.data?.hasError
								"
								[isGradient]="true"
								[isGradientInverted]="true"
								(handleChartRefresh)="onBest10EmissionsChartRefresh()"
							/>
						</div>
					</section>
				}
				@if (authorizationService.canSee(grants.mostEfficientModelWater)) {
					@let best10WaterUsage = best10WaterUsage$ | async;
					<section class="chart">
						<div class="section-title">
							<globalictcc-button
								label="Export chart"
								[textOnly]="true"
								iconName="download"
								(handleClick)="
									onExportChart(
										best10WaterUsageChart.instance,
										'best_10_models_water_usage_per_token'
									)
								"
							/>
						</div>

						<div class="chart-wrapper">
							<globalictcc-chart
								#best10WaterUsageChart
								chartType="barY"
								height="400px"
								[data]="best10WaterUsage?.data?.chart"
								[isLoading]="best10WaterUsage?.isLoading ?? true"
								[hasError]="
									best10WaterUsage?.hasError || best10WaterUsage?.data?.hasError
								"
								[isGradient]="true"
								[isGradientInverted]="true"
								(handleChartRefresh)="onBest10WaterUsageChartRefresh()"
							/>
						</div>
					</section>
				}
			</div>

			<div class="bottom-actions">
				<globalictcc-button
					label="Export report"
					iconName="document"
					[textOnly]="true"
					(handleClick)="onExportReport()"
				/>
				<globalictcc-button
					label="Download as CSV"
					iconName="download"
					[textOnly]="true"
					[disabled]="isExporting"
					(handleClick)="onDownloadCsv()"
				/>
			</div>
		}
	}
</section>

<!-- Dialogs content -->
<ng-template #uploadDeviceList>
	<div class="upload-device-list-dialog-content">
		<span>
			Upload your device list in excel format. Use the template as a reference to correctly
			visualize the data.
		</span>

		<globalictcc-button
			[textOnly]="true"
			label="Download template"
			iconName="download"
			(handleClick)="onDownloadTemplate()"
		/>

		<globalictcc-upload-file
			[errorMessage]="sameNameErrorMessage()"
			[fluid]="true"
			[acceptedFilesExtensions]="['.xlsx']"
			(handleUpload)="onFileUpload($event)"
		/>
	</div>
</ng-template>

<ng-template #rename>
	<div class="rename-input-container">
		<globalictcc-input
			[formControl]="renameFileControl"
			[label]="uploadedFile()?.name || ''"
			[customErrorMessage]="sameNameErrorMessage()"
			(handleInputChange)="onRenameChange($event)"
		/>
	</div>
</ng-template>
